import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { removeManualRowCells } from "@/payroll/ai-script/spreadsheet-cache";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/spreadsheet-sheet-query";
import type { IAiSpreadsheetSheetResponse } from "@/payroll/ai-script/types";
import { workbookMutationRequestConfig } from "@/payroll/ai-script/workbook-mutation";
import { getAiScriptManualRowsPath } from "./paths";
import {
  executeWorkbookMutation,
  invalidateOptimisticSheetOnMutationError,
} from "./workbook-refresh";

interface DeleteManualRowParams {
  runId: string;
  sheetId: string;
  manualRowId: string;
  baseVersion?: number;
}

export const useDeleteManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowId,
      baseVersion,
    }: DeleteManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const config = {
        ...(baseVersion != null
          ? { params: { base_version: baseVersion } }
          : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .delete(
              `${getAiScriptManualRowsPath(businessId, runId)}/${manualRowId}`,
              {
                ...config,
                ...workbookMutationRequestConfig(idempotencyKey),
              },
            )
            .then((res) => res.data),
      });
    },
    onMutate: async ({ runId, sheetId, manualRowId }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        removeManualRowCells(data, manualRowId),
      );

      return { sheetKey };
    },
    onError: (error, _variables, context) => {
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
  });
};

interface RestoreManualRowParams {
  runId: string;
  sheetId: string;
  manualRowId: string;
  baseVersion?: number;
}

export const useRestoreManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowId,
      baseVersion,
    }: RestoreManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = baseVersion != null ? { base_version: baseVersion } : {};
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/${manualRowId}/restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
  });
};

interface BulkManualRowsParams {
  runId: string;
  sheetId: string;
  manualRowIds: string[];
  baseVersion?: number;
}

export const useBulkDeleteManualRows = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowIds,
      baseVersion,
    }: BulkManualRowsParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        manual_row_ids: manualRowIds,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/bulk_destroy`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onMutate: async ({ runId, sheetId, manualRowIds }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        manualRowIds.reduce((acc, id) => removeManualRowCells(acc, id), data),
      );
      return { sheetKey };
    },
    onError: (error, _variables, context) => {
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
  });
};

export const useBulkRestoreManualRows = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      manualRowIds,
      baseVersion,
    }: BulkManualRowsParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = {
        manual_row_ids: manualRowIds,
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        submit: (idempotencyKey) =>
          api
            .post(
              `${getAiScriptManualRowsPath(businessId, runId)}/bulk_restore`,
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
  });
};
